import {
  Close,
  ExpandLess,
  ExpandMore,
  Person,
  Timeline,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatDate, getChileUtcOffset } from 'lib/functions/utils';
import { Evaluation, EvaluationNode } from 'lib/types/models';
import React, { useState } from 'react';

const chileOffsetInMs = getChileUtcOffset() * 60 * 60 * 1000;

interface EvaluationHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  riskData: Evaluation[];
}

const EvaluationHistoryDialog: React.FC<EvaluationHistoryDialogProps> = ({
  open,
  onClose,
  riskData,
}) => {
  const [expandedEvaluation, setExpandedEvaluation] = useState<number | null>(
    null,
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aprobado':
        return 'success';
      case 'Rechazado':
        return 'error';
      default:
        return 'warning';
    }
  };

  const renderSubEngineTooltip = (node: EvaluationNode) => (
    <Box sx={{ p: 2, maxWidth: '360px', minWidth: '300px' }}>
      {/* Header */}
      <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            mb: 0.5,
          }}
        >
          {node.nodeName || 'Nodo de Decisión'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.8rem',
          }}
        >
          Este nodo contiene {node.subDecisionEngineRevisionStory.length}{' '}
          sub-nodos de evaluación
        </Typography>
      </Box>

      {/* Sub-nodes */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {node.subDecisionEngineRevisionStory.map((subNode, index) => (
          <Box
            key={index}
            sx={{
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              p: 1.5,
              border: '1px solid #e0e0e0',
              position: 'relative',
            }}
          >
            {/* Step indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                left: 12,
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            >
              {index + 1}
            </Box>

            {/* Sub-node content */}
            <Box sx={{ mt: 1 }}>
              {subNode.nodeName && subNode.nodeName !== '' && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.85rem',
                    mb: 0.5,
                  }}
                >
                  {subNode.nodeName}
                </Typography>
              )}

              {subNode.resultText && subNode.resultText !== '' && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {subNode.resultText}
                </Typography>
              )}

              {/* Status display */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {subNode.statusNode.severity ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      Decisión:
                    </Typography>
                    <Chip
                      label={subNode.statusNode.name}
                      color={getStatusColor(subNode.statusNode.name)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      Siguiente paso:
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: 'primary.main',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      }}
                    >
                      {subNode.statusNode.name}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderSimpleNodeTooltip = (node: EvaluationNode) => {
    const isDecision =
      node.statusNode.severity !== undefined &&
      node.statusNode.severity !== null;

    return (
      <Box sx={{ p: 2, maxWidth: '360px', minWidth: '280px' }}>
        {/* Header */}
        <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              mb: 0.5,
            }}
          >
            {node.nodeName || 'Nodo de Evaluación'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#2196f3',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              {isDecision ? 'Nodo de Decisión' : 'Nodo de Proceso'}
            </Typography>
          </Box>
        </Box>

        {/* Result text */}
        {node.resultText && node.resultText !== '' && (
          <Box
            sx={{
              backgroundColor: '#f8f9fa',
              p: 1.5,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                lineHeight: 1.5,
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {node.resultText}
            </Typography>
          </Box>
        )}

        {isDecision ? (
          <Box
            sx={{
              backgroundColor: '#e3f2fd',
              p: 1.5,
              borderRadius: 2,
              border: '1px solid #90caf9',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#2196f3',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Decisión
                </Typography>
              </Box>
              <Chip
                label={node.statusNode.name}
                color={getStatusColor(node.statusNode.name)}
                size="small"
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: isDecision ? '#f3e5f5' : '#e3f2fd',
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${isDecision ? '#ce93d8' : '#90caf9'}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: isDecision ? '#9c27b0' : '#2196f3',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {isDecision ? 'Decisión Final' : 'Siguiente Paso'}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: 'white',
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                {node.statusNode.name}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderNodeTooltip = (node: EvaluationNode) => (
    <>
      {node.subDecisionEngineRevisionStory.length > 0
        ? renderSubEngineTooltip(node)
        : renderSimpleNodeTooltip(node)}
    </>
  );

  if (riskData.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
          width: '700px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              p: 0.75,
              borderRadius: '50%',
            }}
          >
            <Timeline sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Historial de Evaluaciones
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {riskData[0].label}
              </Typography>
              <Chip
                label={`${riskData.length || 0} evaluaciones`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {riskData.map((evaluation, index) => (
          <Card
            key={index}
            sx={{
              border: '1px solid #e0e0e0',
              mb: 2,
              boxShadow: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#fafafa',
              },
            }}
            onClick={() =>
              setExpandedEvaluation(expandedEvaluation === index ? null : index)
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.7rem', color: 'secondary.main' }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                    >
                      {evaluation.reviewerName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {formatDate(
                        new Date(evaluation.createdAt + chileOffsetInMs),
                        'large',
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={evaluation.statusNode.name}
                    color={getStatusColor(evaluation.statusNode.name)}
                    size="small"
                    sx={{ height: '25px', fontSize: '0.7rem' }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                    }}
                  >
                    {expandedEvaluation === index ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </Box>
                </Box>
              </Box>

              <Collapse in={expandedEvaluation === index}>
                <Divider sx={{ my: 1.5 }} />
                {evaluation.result && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}
                    >
                      Nodos de Evaluación
                    </Typography>

                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {evaluation.result.map((node, nodeIndex) => (
                        <LightTooltip
                          key={nodeIndex}
                          title={renderNodeTooltip(node)}
                          placement="right"
                        >
                          <Box
                            sx={{
                              p: 1,
                              border: '1px solid #e8e8e8',
                              borderRadius: 1,
                              backgroundColor: '#fafafa',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: '#f0f0f0',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.6rem' }}
                                >
                                  {nodeIndex + 1}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {!node.nodeName || node.nodeName === ''
                                    ? 'Sin comentarios'
                                    : node.nodeName}
                                </Typography>
                                {node.resultText && node.resultText !== '' && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.65rem',
                                      fontStyle: 'italic',
                                      color: '#666666',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                      maxWidth: '275px',
                                      minWidth: '80px',
                                    }}
                                  >
                                    {node.resultText}
                                  </Typography>
                                )}
                              </Box>
                              {node.statusNode.name &&
                                node.statusNode.severity && (
                                  <Chip
                                    label={node.statusNode.name}
                                    color={getStatusColor(node.statusNode.name)}
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '0.6rem',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                            </Box>
                          </Box>
                        </LightTooltip>
                      ))}
                    </Box>
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationHistoryDialog;
